import Decimal from "decimal.js";

import { useCallback, useEffect, useState, type ReactNode } from "react";

import { useProduct } from "@/features/product/product-context";
import { OrderFormContext, type FieldValues } from "./order-form-context";

type OrderFormProviderProps = {
  children: ReactNode;
};

const fixValueToPrecision = (value: string, precision: number) => {
  const [intPart, decPart = ""] = value.split(".");
  if (decPart) {
    const limitedDecPart = decPart.slice(0, precision);
    return `${intPart}.${limitedDecPart}`;
  }

  return value;
};

const fixTotalToPrecision = (value: string, precision: number) => {
  const [intPart, decPart = ""] = value.split(".");
  if (decPart) {
    const limitedDecPart = decPart.slice(0, precision);
    return `${BigInt(intPart).toString()}.${limitedDecPart}`;
  }

  return BigInt(value).toString();
};

const calculateTotal = (
  precision: number,
  limitPrice?: string,
  amount?: string
) => {
  if (!limitPrice || !amount) {
    return;
  }

  const result = new Decimal(limitPrice).mul(amount);
  const fixedValue = fixTotalToPrecision(result.toFixed(precision), precision);

  return fixedValue;
};

export const OrderFormProvider = ({ children }: OrderFormProviderProps) => {
  const { product } = useProduct();
  const [fieldValues, setFieldValues] = useState<FieldValues>({});

  const setLimitPrice = useCallback(
    (value: string) => {
      if (!product?.quote.precision) {
        return;
      }

      const newLimitPrice = fixValueToPrecision(
        value,
        product?.quote.precision
      );

      const newTotal = calculateTotal(
        product?.quote.precision,
        newLimitPrice,
        fieldValues.amount
      );

      setFieldValues((v) => ({
        ...v,
        limitPrice: newLimitPrice,
        total: newTotal,
      }));
    },
    [setFieldValues, fieldValues.amount, product?.quote.precision]
  );

  const setAmount = useCallback(
    (value: string) => {
      if (!product?.base.precision) {
        return;
      }

      const newAmount = fixValueToPrecision(value, product?.base.precision);

      const newTotal = calculateTotal(
        product?.quote.precision,
        fieldValues.limitPrice,
        newAmount
      );

      setFieldValues((v) => ({ ...v, amount: newAmount, total: newTotal }));
    },
    [
      setFieldValues,
      fieldValues.limitPrice,
      product?.base.precision,
      product?.quote.precision,
    ]
  );

  // reset
  useEffect(() => {
    setFieldValues({});
  }, [product]);

  return (
    <OrderFormContext.Provider
      value={{ fieldValues, setAmount, setLimitPrice }}
    >
      {children}
    </OrderFormContext.Provider>
  );
};
